import { get } from 'svelte/store'
import {
  createClient as createPrismicClient,
  PreviewTokenExpiredError,
  NotFoundError
} from '@prismicio/client'

import { prefix as defaultPrefix } from '$lib/utils/prefix.js'

export const repositoryName = 'allaboard'

/**
 * @param {any} err
 * @returns {{ status: import('@sveltejs/kit').NumericRange<400, 599>, message: string }}
 */
export function handleError(err) {
  let status = 'status' in err ? err.status : 500
  if (err instanceof NotFoundError) status = 404
  if (err instanceof PreviewTokenExpiredError) status = 400

  let message = 'Something’s wrong'
  if (err instanceof NotFoundError) message = 'Page not found'
  if (err instanceof PreviewTokenExpiredError) message = 'Preview has expired'

  return { status, message }
}

/**
 * @param {Prismic.PrismicDocument|Prismic.FilledContentRelationshipField|Prismic.FilledLinkToMediaField|Prismic.FilledLinkToWebField|Prismic.AlternateLanguage} doc
 * @param {string} [prefix]
 */
export function resolve(doc, prefix = get(defaultPrefix)) {
  if (!doc) return null
  if ('link_type' in doc) {
    // @ts-ignore
    if (doc.link_type === 'Any') return null
    if (doc.link_type === 'Media') return doc.url
    if (doc.link_type === 'Web') return doc.url.replace(/^https?:\/\/#/, '')
    if (doc.link_type === 'Document' && doc.isBroken) return null
  }

  switch (doc.type) {
    case 'checkout':
    case 'settings':
    case 'homepage':
      return prefix || '/'
    case 'operator':
      return `/carriers/${doc.uid}`
    case 'dashboard':
      return `/agent`
    case 'trip_planner':
      return `${prefix}/search`
    case 'route':
      return `${prefix}/routes/${doc.uid}`
    case 'page':
      return `${prefix}/${doc.uid}`
    default:
      return null
  }
}

/**
 * @param {{ request?: Request }} props
 * @returns {Prismic.Client}
 */
export function createClient({ request }) {
  const client = createPrismicClient(repositoryName)
  if (request) client.enableAutoPreviewsFromReq(request)
  else client.enableAutoPreviews()
  return client
}

export function resolveLang(lang) {
  switch (lang) {
    case 'en':
      return 'en-gb'
    case 'sv':
      return 'sv-se'
    case 'da':
      return 'da-dk'
    case 'de':
      return 'de-de'
    case 'fr':
      return 'fr-fr'
    case 'es':
      return 'es-es'
    default:
      return lang
  }
}

export const pageQuery = `
  {
    page {
      ...pageFields
      body {
        ...on body {
          non-repeat {
            gray
            larger
            content
          }
          repeat {
            content
          }
        }
        ...on icon_grid {
          non-repeat {
            gray
          }
          repeat {
            icon_body
            icon_title
            icon
          }
        }
        ...on journey_search_hero {
          non-repeat {
            heading
            description
            image
          }
        }
        ...on trip_search_form {
          non-repeat {
            ...non-repeatFields
          }
        }
        ...on divider {
          non-repeat {
            ...non-repeatFields
          }
        }
        ...on callout {
          non-repeat {
            gray
            text
            scroll_hook
            scroll_text
          }
        }
        ...on illustrated_text {
          non-repeat {
            gray
            image
            video
            flip
            small
            content
          }
        }
        ...on banner {
          non-repeat {
            gray
            content
            backdrop
            white
            image
            video
            cta_text
            cta_link
            cta_supporting_text
            cta_supporting_link
            include_trip_search_form
          }
        }
        ...on pages_link_grid {
          non-repeat {
            gray
          }
          repeat {
            link {
              ...on page {
                title
                description
                featured_image
                body {
                  ...on hero {
                    non-repeat {
                      image
                    }
                  }
                }
              }
              ...on category {
                title
                description
                featured_image
              }
              ...on post {
                title
                description
              }
            }
          }
        }
        ...on scroll_hook {
          non-repeat {
            id
          }
        }
        ...on section_title {
          non-repeat {
            gray
            content
          }
        }
        ...on quote {
          non-repeat {
            gray
            text
            cite
          }
        }
        ...on image {
          non-repeat {
            gray
            image
            caption
          }
        }
        ...on list_columns {
          non-repeat {
            icon
            gray
            color
          }
          repeat {
            item
          }
        }
        ...on button {
          non-repeat {
            link
            text
          }
        }
        ...on operators {
          non-repeat {
            gray
            content
            flip
          }
          repeat {
            operator {
              ...on operator {
                name
                logo
              }
            }
          }
        }
        ...on hero {
          non-repeat {
            tagline
            image
            heading
            preamble
          }
          repeat {
            button_text
            button_hook
            button_link
          }
        }
        ...on page_heading {
          non-repeat {
            tagline
            heading
            preamble
            promotion
            large
          }
          repeat {
            button_text
            button_hook
            button_link
          }
        }
        ...on image_grid {
          non-repeat {
            gray
          }
          repeat {
            image
            image_title
            text
            link
            link_text
          }
        }
        ...on blog_post_preamble {
          non-repeat {
            author_name
            author_image
            publish_date
          }
        }
        ...on article_list {
          non-repeat {
            gray
            expanded
          }
          repeat {
            page {
              ...on page {
                title
                description
                featured_image
                body {
                  ...on blog_post_preamble {
                    non-repeat {
                      author_name
                      author_image
                      publish_date
                    }
                  }
                  ...on page_heading {
                    non-repeat {
                      heading
                      preamble
                    }
                  }
                  ...on section_title {
                    non-repeat {
                      content
                    }
                  }
                  ...on body {
                    non-repeat {
                      content
                    }
                  }
                }
              }
            }
          }
        }
        ...on interrail_shop {
          non-repeat {
            product
            show_all_passes
            campaign_id
            badge_text
            current_deal_heading
            current_deal_text
            current_deal_image
          }
        }
        ...on contact_form {
          non-repeat {
            description
            twist_channel_link
            button_text
            success_message
            gray
          }
          repeat {
            label
            type
            options
            required
            placeholder
            is_primary_key
          }
        }
        ...on signup_form {
          non-repeat {
            content
            extra_content
            success_message
          }
          repeat {
            list_item
          }
        }
        ...on advertisement {
          non-repeat {
            background_color
            image_overflow
            link_text
            heading
            image
            label
            link
          }
        }
      }
    }
  }
`
